@if (panel; as map) {
  @if (isVisible(map)) {
    <div
      data-testid="mindmap-zoom-panel"
      class="zoom-control-panel"
      [class.is-expanded]="isPanelExpanded()"
      (mouseenter)="onPanelMouseEnter()"
      (mouseleave)="onPanelMouseLeave()"
    >
      @if (map.outlineEnabled()) {
        <div class="mode-dropdown-container">
          <button
            data-testid="mindmap-mode-toggle"
            type="button"
            class="mode-btn"
            [title]="i18n.t().modeSelectTitle"
            [attr.aria-label]="i18n.t().modeSelectAria"
            [attr.aria-expanded]="modeDropdownOpen()"
            aria-haspopup="menu"
            (click)="toggleModeDropdown()"
          >
            <svg
              class="sa-icon sa-nofill"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use
                [attr.href]="'/assets/icons/sprite.svg#' + currentModeIcon()"
              ></use>
            </svg>
            <svg
              class="sa-icon sa-nofill mode-btn-chevron"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use href="/assets/icons/sprite.svg#chevron-down"></use>
            </svg>
          </button>

          @if (modeDropdownOpen()) {
            <div
              class="mode-dropdown"
              role="menu"
              [attr.aria-label]="i18n.t().modeSelectAria"
            >
              <button
                data-testid="mindmap-mode-map"
                type="button"
                class="mode-option"
                [class.active]="isModeActive('mindmap')"
                [title]="i18n.t().mindMapModeTitle"
                [attr.aria-label]="i18n.t().switchToMindMap"
                role="menuitem"
                (click)="onSelectMode(map, 'mindmap')"
              >
                <svg
                  class="sa-icon sa-nofill"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <use href="/assets/icons/sprite.svg#git-branch"></use>
                </svg>
                <span>{{ i18n.t().mindMapModeLabel }}</span>
              </button>
              <button
                data-testid="mindmap-mode-outline"
                type="button"
                class="mode-option"
                [class.active]="isModeActive('outline')"
                [title]="i18n.t().outlineModeTitle"
                [attr.aria-label]="i18n.t().switchToOutline"
                role="menuitem"
                (click)="onSelectMode(map, 'outline')"
              >
                <svg
                  class="sa-icon sa-nofill"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <use href="/assets/icons/sprite.svg#list"></use>
                </svg>
                <span>{{ i18n.t().outlineModeLabel }}</span>
              </button>
              <button
                data-testid="mindmap-mode-focus"
                type="button"
                class="mode-option"
                [class.active]="isModeActive('focus')"
                [title]="i18n.t().focusModeTitle"
                [attr.aria-label]="i18n.t().switchToFocus"
                role="menuitem"
                (click)="onSelectMode(map, 'focus')"
              >
                <svg
                  class="sa-icon sa-thin"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <use href="/assets/icons/sprite.svg#target"></use>
                </svg>
                <span>{{ i18n.t().focusModeLabel }}</span>
              </button>
            </div>
          }
        </div>

        @if (map.mode() === "map" && isDetailControlAvailable()) {
          <div class="show-detail-container">
            <button
              data-testid="mindmap-show-detail"
              class="zoom-control-btn show-detail"
              type="button"
              title="Show Detail"
              aria-label="Show Detail"
              [attr.aria-expanded]="detailPanelOpen()"
              (click)="toggleDetailPanel()"
            >
              <svg
                class="sa-icon sa-nofill"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <use href="/assets/icons/sprite.svg#layers"></use>
              </svg>
            </button>

            @if (detailPanelOpen()) {
              <div
                class="detail-popover"
                role="dialog"
                aria-label="Show detail"
              >
                <div class="detail-title">Show detail</div>
                <div class="detail-controls">
                  <input
                    data-testid="mindmap-detail-range"
                    class="detail-range"
                    type="range"
                    [min]="detailMin()"
                    [max]="detailMax()"
                    [value]="detailCount()"
                    aria-label="Show detail node count"
                    (input)="onDetailCountInput($event)"
                  />
                  <input
                    data-testid="mindmap-detail-number"
                    class="detail-number form-control"
                    type="number"
                    [min]="detailMin()"
                    [max]="detailMax()"
                    [value]="detailCount()"
                    aria-label="Show detail node count"
                    (input)="onDetailCountInput($event)"
                  />
                </div>
              </div>
            }
          </div>
        }

        <div
          class="zoom-divider"
          [class.show-detail-divider]="
            map.mode() === 'map' && isDetailControlAvailable()
          "
        ></div>
      }

      @if (map.mode() === "map") {
        <div class="zoom-actions">
          <button
            data-testid="mindmap-zoom-in"
            class="zoom-control-btn zoom-in"
            type="button"
            (click)="onZoomIn()"
            [title]="i18n.t().zoomInTitle"
            [attr.aria-label]="i18n.t().zoomInAria"
          >
            <svg
              class="sa-icon sa-nofill"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use href="/assets/icons/sprite.svg#plus"></use>
            </svg>
          </button>

          <div class="zoom-level-container">
            <button
              data-testid="mindmap-zoom-level"
              class="zoom-level"
              type="button"
              (click)="toggleDropdown()"
              [title]="i18n.t().zoomSelectTitle"
            >
              {{ zoomLevel() }}%
            </button>

            @if (zoomDropdownOpen()) {
              <div class="zoom-dropdown">
                @for (option of zoomOptions; track option.value) {
                  <button
                    [attr.data-testid]="'mindmap-zoom-option-' + option.value"
                    class="zoom-option"
                    [class.active]="zoomLevel() === option.value"
                    (click)="setZoomLevel(option.value)"
                    type="button"
                  >
                    {{ option.label }}
                  </button>
                }
                <div class="zoom-dropdown-divider"></div>
                <button
                  data-testid="mindmap-fit-map"
                  class="zoom-option fit-map"
                  (click)="fitMap()"
                  type="button"
                >
                  {{ i18n.t().fitMap }}
                </button>
              </div>
            }
          </div>

          <button
            data-testid="mindmap-zoom-out"
            class="zoom-control-btn zoom-out"
            type="button"
            (click)="onZoomOut()"
            [title]="i18n.t().zoomOutTitle"
            [attr.aria-label]="i18n.t().zoomOutAria"
          >
            <svg
              class="sa-icon sa-nofill"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use href="/assets/icons/sprite.svg#minus"></use>
            </svg>
          </button>

          <div class="zoom-divider"></div>

          <button
            data-testid="mindmap-reset-zoom"
            class="zoom-control-btn reset-zoom"
            type="button"
            (click)="onResetZoom()"
            [title]="i18n.t().resetZoomTitle"
            [attr.aria-label]="i18n.t().resetZoomAria"
          >
            <svg
              class="sa-icon sa-nofill"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use href="/assets/icons/sprite.svg#refresh-ccw"></use>
            </svg>
          </button>

          <button
            data-testid="mindmap-center-map"
            class="zoom-control-btn center"
            type="button"
            (click)="onCenter()"
            [title]="i18n.t().centerMapTitle"
            [attr.aria-label]="i18n.t().centerMapAria"
          >
            <svg
              class="sa-icon sa-nofill"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use href="/assets/icons/sprite.svg#crosshair"></use>
            </svg>
          </button>
        </div>
      }
    </div>
  }
}
